/**
 * As peças pequenas que toda tela repete: o vazio, a espera e o aviso.
 *
 * Vazio e espera são coisas diferentes e ficam parecidas quando descuidadas.
 * Ter as duas prontas aqui é o que impede uma tela de dizer "nada por aqui"
 * enquanto os dados ainda estão a caminho.
 */

import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icone, type NomeDoIcone } from './Icone';
import { traduzir } from '@nosso-sempre/shared';

export function Esqueleto({ altura = 72, quantos = 3 }: { altura?: number; quantos?: number }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--e-sm)' }} aria-hidden="true">
      {Array.from({ length: quantos }, (_, i) => (
        <div key={i} className="esqueleto" style={{ height: altura }} />
      ))}
    </div>
  );
}

export function Vazio({
  icone,
  titulo,
  recado,
  acao,
}: {
  icone: NomeDoIcone;
  titulo: string;
  recado?: string;
  acao?: ReactNode;
}) {
  return (
    <div
      className="cartao"
      style={{
        display: 'grid',
        gap: 'var(--e-sm)',
        justifyItems: 'center',
        textAlign: 'center',
        padding: 'var(--e-xl) var(--e-md)',
        color: 'var(--lavanda)',
      }}
    >
      <Icone nome={icone} tamanho={28} />
      <h3>{titulo}</h3>
      {recado ? <p className="discreto">{recado}</p> : null}
      {acao}
    </div>
  );
}

export function Aviso({ children }: { children: ReactNode }) {
  return (
    <p
      className="erro"
      role="alert"
      style={{ display: 'flex', alignItems: 'center', gap: 'var(--e-sm)' }}
    >
      <Icone nome="aviso" tamanho={16} />
      {children}
    </p>
  );
}

/**
 * O cabeçalho de uma tela, que é o `NossoSempreFeatureHeader` do Flutter:
 * uma faixa de vidro com a seta de voltar, o círculo da cor da seção com o
 * desenho dela dentro, o nome, o subtítulo e o que a tela quiser à direita.
 *
 * A seta nunca some, pelo mesmo motivo de lá (`Voltar.agora`): link direto e
 * recarregar chegam sem pilha nenhuma, e uma seta que às vezes aparece deixa
 * a pessoa presa. Sem pilha, o lugar de trás de uma tela de primeiro nível é
 * o painel, e o de uma tela de dentro é a primeira parte do endereço.
 */
export function Titulo({
  children,
  recado,
  icone,
  cor,
  acao,
}: {
  children: ReactNode;
  recado?: string;
  icone?: NomeDoIcone;
  cor?: string;
  acao?: ReactNode;
}) {
  const navegar = useNavigate();
  const { pathname } = useLocation();

  function voltar() {
    const partes = pathname.split('/').filter(Boolean);
    navegar(partes.length > 1 ? `/${partes[0]}` : '/');
  }

  return (
    <header className="capa-tela" style={cor ? { ['--cor-secao' as string]: cor } : undefined}>
      <button className="capa-voltar" type="button" onClick={voltar}>
        <Icone nome="voltar" tamanho={17} rotulo={traduzir('Voltar')} />
      </button>
      {icone ? (
        <span className="capa-selo" aria-hidden="true">
          <Icone nome={icone} tamanho={19} />
        </span>
      ) : null}
      <div className="capa-nomes">
        <h1>{children}</h1>
        {recado ? <p>{recado}</p> : null}
      </div>
      {acao}
    </header>
  );
}
